import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useMemo } from "react";
import { POINTER_BODY_CLASS } from "./pointerBody";
import { SPOTLIGHT_SURFACE_ATTRIBUTE } from "./useDomPresence";
import { useElementRect } from "./useElementRect";
import { usePrefersReducedMotion } from "./useMediaMatch";

const RING_PADDING = 4;

/**
 * FeatureSpotlightPointer: o anel pulsante em volta do recurso e o cartão compacto preso a ele
 * (título, um texto curto de até 3 linhas, "Entendi"). Não bloqueia nada: o anel não captura o mouse e o
 * cartão não prende o foco nem fecha por Esc ou clique fora.
 *
 * Com preferência por movimento reduzido o anel fica parado.
 *
 * @param {{
 *   element: Element,
 *   title: string,
 *   body: string,
 *   stepLabel?: string|null,
 *   insideModal?: boolean,
 *   titleId?: string,
 *   onAcknowledge: () => void,
 * }} props
 */
export default function FeatureSpotlightPointer({ element, title, body, stepLabel = null, insideModal = false, titleId, onAcknowledge }) {
  const rect = useElementRect(element);
  const reducedMotion = usePrefersReducedMotion();
  const anchorRef = useMemo(() => ({ current: element }), [element]);

  return (
    <>
      {rect && (
        <div
          data-spotlight-ring
          aria-hidden="true"
          className={cn(
            "pointer-events-none fixed rounded-xl ring-2 ring-cyan-400 shadow-[0_0_0_4px_rgb(34_211_238/0.25)]",
            // Dentro de um diálogo (z-50) o anel e o cartão precisam ficar por cima dele.
            insideModal ? "z-[60]" : "z-40",
            !reducedMotion && "animate-pulse",
          )}
          style={{
            top: rect.top - RING_PADDING,
            left: rect.left - RING_PADDING,
            width: rect.width + RING_PADDING * 2,
            height: rect.height + RING_PADDING * 2,
          }}
        />
      )}
      {/* O Radix copia o z-index do cartão uma vez, ao montar: quem entra num diálogo (z-50) remonta o
          cartão, que então nasce acima dele (e depois do `aria-hidden` que o diálogo põe nos irmãos). */}
      <Popover key={insideModal ? "in-modal" : "on-page"} open>
        <PopoverAnchor virtualRef={anchorRef} />
        <PopoverContent
          side="bottom"
          align="start"
          sideOffset={14}
          aria-labelledby={titleId}
          data-variant="pointer"
          {...{ [SPOTLIGHT_SURFACE_ATTRIBUTE]: "" }}
          className={cn("w-72 max-w-[calc(100vw-2rem)] border-border-strong p-3.5 shadow-lg", insideModal && "z-[61]")}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDown={insideModal ? keepFromDismissingTheModal : undefined}
        >
          {/* Igual ao cartão do menu: o Tab não pode ficar preso no laço de foco do Popover. */}
          <div onKeyDown={letTabLeave} className="space-y-1.5">
            {stepLabel && <p className="text-xs font-medium uppercase tracking-wide text-cyan-400">{stepLabel}</p>}
            <h2 id={titleId} className="text-sm font-semibold leading-snug text-foreground-strong">
              {title}
            </h2>
            <p className={POINTER_BODY_CLASS} title={body}>
              {body}
            </p>
            <div className="flex justify-end pt-1">
              <Button type="button" size="sm" onClick={onAcknowledge}>
                Entendi
              </Button>
            </div>
          </div>
          <PopoverPrimitive.Arrow width={18} height={9} className="fill-popover stroke-border-strong" />
        </PopoverContent>
      </Popover>
    </>
  );
}

function letTabLeave(event) {
  if (event.key === "Tab") event.stopPropagation();
}

/**
 * Um diálogo do Radix fecha quando alguém clica fora dele, e o cartão (que mora no fim do body)
 * está fora. O Radix escuta o `pointerdown` no document, então o clique no cartão para de subir
 * aqui, antes de chegar lá. O clique em si (o "Entendi") não é afetado.
 */
function keepFromDismissingTheModal(event) {
  event.nativeEvent.stopPropagation();
}
